import * as THREE from "three";
import type { BlockRegistry } from "../../sim/world/types";
import type { BlockMeshDef, PackedMeshGroup, RenderGroup } from "../meshing/types";
import { colorForBlockName } from "./palette";

/**
 * Mesh definitions for a block registry: albedo and render group per block.
 *
 * There is no texture atlas any more. The references are a flat-colour
 * micro-voxel world whose richness comes from per-voxel shade variation, and an
 * atlas gives the opposite — one uniform colour per block type, which renders
 * as plastic no matter how good the lighting is. The generated PNG set and its
 * manifest were deleted with the loader that read them.
 */
export function meshDefinitionsFromRegistry(registry: BlockRegistry): BlockMeshDef[] {
  return registry.all.map((definition): BlockMeshDef => {
    const palette = colorForBlockName(definition.name);
    return {
      id: definition.id,
      opaque: definition.opaque,
      group: definition.name === "water" ? "water" : definition.opaque ? "opaque" : "transparent",
      color: palette.rgb,
      colorJitter: palette.jitter,
      columnShade: palette.columnShade ?? false,
      sway: palette.sway ?? false,
    };
  });
}

const VERTEX_SHADER = `
precision highp float;
precision highp int;
in uint attr0;
in uint attr1;
in uint attr2;
uniform mat4 modelViewMatrix;
uniform mat4 modelMatrix;
uniform mat4 projectionMatrix;
uniform float time;
uniform float windStrength;
uniform float waterMode;
out vec3 vColor;
out vec4 vLight;
out float vAo;
out vec3 vNormal;
out float vFogDepth;
out vec3 vWorld;
out vec3 vView;

void main() {
  vec3 p = vec3(float(attr0 & 63u), float((attr0 >> 6u) & 63u), float((attr0 >> 12u) & 63u));
  uint normal = (attr0 >> 18u) & 7u;
  uint ao = (attr0 >> 21u) & 3u;
  vColor = vec3(
    float((attr2 >> 16u) & 255u),
    float((attr2 >> 8u) & 255u),
    float(attr2 & 255u)
  ) / 255.0;
  vLight = vec4(
    float((attr1 >> 12u) & 15u),
    float((attr1 >> 16u) & 15u),
    float((attr1 >> 20u) & 15u),
    float((attr1 >> 24u) & 15u)
  ) / 15.0;
  vAo = float(ao) / 3.0;
  if (normal == 0u) { vNormal = vec3(1,0,0); }
  else if (normal == 1u) { vNormal = vec3(-1,0,0); }
  else if (normal == 2u) { vNormal = vec3(0,1,0); }
  else if (normal == 3u) { vNormal = vec3(0,-1,0); }
  else if (normal == 4u) { vNormal = vec3(0,0,1); }
  else { vNormal = vec3(0,0,-1); }
  // Wind. Driven by world position so gusts travel across the meadow as one
  // field instead of each chunk waving to its own beat, and scaled by the sway
  // nibble so a blade bends at the tip and stays rooted.
  // Rise above the blade's root in voxels. Quadratic, so the bend is almost
  // nothing near the ground and grows toward the tip: that is the shape grass
  // actually takes, and it keeps any residual mismatch between neighbouring
  // blades far below a pixel down where they meet the ground.
  float rise = float(attr1 & 15u);
  float sway = rise * rise / 64.0;
  if (sway > 0.0) {
    vec3 worldPosition = (modelMatrix * vec4(p, 1.0)).xyz;
    float gust = 0.65 + 0.35 * sin(worldPosition.x * 0.012 + worldPosition.z * 0.009 + time * 0.55);
    float phase = worldPosition.x * 0.09 + worldPosition.z * 0.11 + time * 2.1;
    float bend = sway * windStrength * gust;
    p.x += sin(phase) * bend;
    p.z += sin(phase * 0.77 + 1.3) * bend * 0.7;
  }
  vec3 world = (modelMatrix * vec4(p, 1.0)).xyz;
  // Water surface. Only the upward faces move, so the sides of the body stay
  // welded to the bed and no gap can open under the swell.
  if (waterMode > 0.5 && vNormal.y > 0.5) {
    // Short, crossed wavelets rather than one long swell. A low-frequency sine
    // over a wide sheet draws concentric contour arcs across it, which reads as
    // a topographic map, not water. These are 1-3 m and travel in three
    // directions so no pattern is legible.
    float swell = sin(world.x * 0.42 + world.z * 0.13 + time * 2.6) * 0.34
      + sin(world.z * 0.51 - world.x * 0.19 - time * 2.1) * 0.28
      + sin((world.x + world.z) * 0.27 + time * 3.3) * 0.18;
    p.y += swell * 0.5;
    world = (modelMatrix * vec4(p, 1.0)).xyz;
  }
  vWorld = world;
  vec4 viewPosition = modelViewMatrix * vec4(p, 1.0);
  vView = -viewPosition.xyz;
  vFogDepth = length(viewPosition.xyz);
  gl_Position = projectionMatrix * viewPosition;
}`;

const FRAGMENT_SHADER = `
precision highp float;
precision highp int;
uniform vec3 sunDirection;
uniform vec3 sunColor;
uniform vec3 ambientColor;
uniform vec3 groundColor;
uniform float aoStrength;
uniform float surfaceAlpha;
uniform vec3 fogColor;
uniform float fogDensity;
uniform float horizonFogDensity;
in vec3 vColor;
in vec4 vLight;
in float vAo;
in vec3 vNormal;
in float vFogDepth;
in vec3 vWorld;
in vec3 vView;
uniform float waterMode;
uniform float time;
out vec4 outColor;

void main() {
  vec3 normal = normalize(vNormal);
  float alpha = surfaceAlpha;

  // Water gets a real surface rather than a flat tinted quad: a rippled normal,
  // a sun glint, and a Fresnel term that turns it reflective at grazing angles
  // and clear when you look straight down into it. That angle dependence is
  // most of what makes water read as water.
  vec3 waterTint = vec3(0.0);
  float specular = 0.0;
  if (waterMode > 0.5 && normal.y > 0.5) {
    float rippleX = sin(vWorld.x * 1.3 + vWorld.z * 0.4 + time * 3.1)
      + 0.6 * sin(vWorld.z * 0.9 - time * 2.4)
      + 0.35 * sin((vWorld.x - vWorld.z) * 2.1 + time * 4.7);
    float rippleZ = cos(vWorld.z * 1.2 - vWorld.x * 0.5 + time * 2.7)
      + 0.6 * cos(vWorld.x * 0.85 + time * 2.0)
      + 0.35 * cos((vWorld.x + vWorld.z) * 1.9 - time * 4.1);
    normal = normalize(normal + vec3(rippleX * 0.16, 0.0, rippleZ * 0.16));
    vec3 view = normalize(vView);
    float facing = clamp(dot(normal, view), 0.0, 1.0);
    float fresnel = pow(1.0 - facing, 4.0);
    alpha = mix(0.5, 0.97, fresnel);
    // Sun glint, narrow and bright, riding the ripple normal.
    vec3 halfway = normalize(normalize(sunDirection) + view);
    specular = pow(max(0.0, dot(normal, halfway)), 140.0) * 2.4;
    waterTint = fogColor * fresnel * 0.55;
  }

  float sky = vLight.x;
  float sun = max(0.0, dot(normal, normalize(sunDirection))) * sky;
  vec3 blockLight = vLight.yzw;
  // Ambient is the sky, so it has to be occluded by the sky channel too. Left
  // unoccluded it puts a hard floor under every surface and a forest canopy
  // makes no difference to the ground beneath it — which is the opposite of the
  // references, where that shade is most of the depth in the image. The
  // residual term is bounced light, which does reach under a crown.
  /*
   * Hemisphere ambient: sky from above, bounced ground light from below.
   *
   * A single ambient colour gives every upward face one tone and every vertical
   * face another, so a flat-coloured voxel world resolves into two flat greys
   * and reads as cardboard. Letting the ambient swing from cool sky overhead to
   * warm bounce underneath gives each of the six face directions its own value,
   * which is where the form in the references comes from — there is no other
   * shading cue on an untextured cube.
   */
  float upness = normal.y * 0.5 + 0.5;
  vec3 hemisphere = mix(groundColor, ambientColor, upness * upness);
  /*
   * The floor under the sky term, and the floor under AO, are what keep shade
   * *coloured* rather than black.
   *
   * These two multiply, and at 0.42/0.3 they multiplied to 0.126: a canopy-side
   * leaf came out at 9% value where the references hold their deepest foliage
   * at 18-25%, still plainly green. Measured over the vibeshots, foliage value
   * spans roughly p5 11% to p95 50-62% — a range of about 5x, not the 20x that
   * stacking a hard sky occlusion on a hard AO gives you.
   *
   * The sun and full-sky case is deliberately untouched: 0.52 + 0.48 still sums
   * to 1.0 at sky = 1, so nothing that was correctly exposed moves. Only the
   * shadowed end lifts.
   */
  /*
   * The sun's weight sets the *range* of the image, and only the lit end of it.
   *
   * Once the shade floor above was right, the deepest foliage measured 11-15%
   * value against the references' 10-13% — correct — while the brightest measured
   * 35-46% against their 49-62%. That is not a global exposure problem, which
   * would have moved both: it is too little sun. Raising the ambient to chase it
   * lifts the floor along with the ceiling and flattens the image; raising the sun
   * opens the two apart, which is also what makes the light read as coming from
   * somewhere.
   */
  vec3 lighting = hemisphere * (0.52 + 0.48 * sky) + sunColor * (sun * 0.85) + blockLight;
  // Contact shading is most of what gives a flat-coloured voxel world its form,
  // and at eight voxels to the metre there are a great many contacts — so this
  // stays deeper and wider than a pure three-neighbour curve, just no longer
  // deep enough to take the hue out with the light.
  float ao = mix(1.0, mix(1.0, 0.50, pow(vAo, 0.75)), aoStrength);
  vec3 litColor = vColor * lighting * ao + waterTint + vec3(specular) * sunColor * sky;
  float distanceFog = 1.0 - exp(-pow(fogDensity * vFogDepth, 2.0));
  float horizonFog = 1.0 - exp(-pow(horizonFogDensity * max(0.0, vFogDepth - 72.0), 2.0));
  float fogFactor = clamp(1.0 - (1.0 - distanceFog) * (1.0 - horizonFog * 0.38), 0.0, 1.0);
  outColor = vec4(mix(litColor, fogColor, fogFactor), alpha);
}`;

export class BlockMaterialSet {
  readonly opaque: THREE.RawShaderMaterial;
  readonly cutout: THREE.RawShaderMaterial;
  readonly transparent: THREE.RawShaderMaterial;
  readonly water: THREE.RawShaderMaterial;

  constructor() {
    this.opaque = this.createMaterial("opaque");
    this.cutout = this.createMaterial("cutout");
    this.transparent = this.createMaterial("transparent");
    this.water = this.createMaterial("water");
  }

  material(group: RenderGroup): THREE.RawShaderMaterial {
    return this[group];
  }

  setSun(direction: THREE.Vector3, color: THREE.Color, sky: THREE.Color, ground: THREE.Color): void {
    for (const material of [this.opaque, this.cutout, this.transparent, this.water]) {
      material.uniforms["sunDirection"]!.value.copy(direction);
      material.uniforms["sunColor"]!.value.copy(color);
      material.uniforms["ambientColor"]!.value.copy(sky);
      material.uniforms["groundColor"]!.value.copy(ground);
    }
  }

  setFog(color: THREE.Color, density: number, horizonDensity = density * 1.7): void {
    for (const material of [this.opaque, this.cutout, this.transparent, this.water]) {
      material.uniforms["fogColor"]!.value.copy(color);
      material.uniforms["fogDensity"]!.value = density;
      material.uniforms["horizonFogDensity"]!.value = horizonDensity;
    }
  }

  /** Advances the wind. Call once per frame with seconds since start. */
  setTime(seconds: number): void {
    for (const material of [this.opaque, this.cutout, this.transparent, this.water]) {
      material.uniforms["time"]!.value = seconds;
    }
  }

  setAmbientOcclusion(enabled: boolean): void {
    for (const material of [this.opaque, this.cutout, this.transparent, this.water]) {
      material.uniforms["aoStrength"]!.value = enabled ? 1 : 0;
    }
  }

  dispose(): void {
    this.opaque.dispose();
    this.cutout.dispose();
    this.transparent.dispose();
    this.water.dispose();
  }

  private createMaterial(group: RenderGroup): THREE.RawShaderMaterial {
    const blended = group === "transparent" || group === "water";
    return new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      uniforms: {
        sunDirection: { value: new THREE.Vector3(0.4, 0.8, 0.2) },
        sunColor: { value: new THREE.Color(0xffffff) },
        // The calibrated pair, matching main.ts. The palette in palette.ts was
        // solved against exactly these, so a page that forgets to call setSun
        // still renders the colours the palette was authored for.
        ambientColor: { value: new THREE.Color(0xd2d6d1) },
        groundColor: { value: new THREE.Color(0x838c6d) },
        aoStrength: { value: 1 },
        time: { value: 0 },
        // In voxels: a blade tip travels about a quarter of a metre.
        windStrength: { value: 2.0 },
        surfaceAlpha: { value: group === "water" ? 0.72 : group === "transparent" ? 0.55 : 1 },
        waterMode: { value: group === "water" ? 1 : 0 },
        fogColor: { value: new THREE.Color(0x92b8d8) },
        fogDensity: { value: 0.003 },
        horizonFogDensity: { value: 0.00525 },
      },
      transparent: blended,
      depthWrite: !blended,
      side: group === "water" ? THREE.DoubleSide : THREE.FrontSide,
    });
  }
}

export function geometryFromPackedMesh(group: PackedMeshGroup): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  const first = new THREE.Uint32BufferAttribute(new Uint32Array(group.attr0), 1);
  const second = new THREE.Uint32BufferAttribute(new Uint32Array(group.attr1), 1);
  const third = new THREE.Uint32BufferAttribute(new Uint32Array(group.attr2), 1);
  first.gpuType = THREE.IntType;
  second.gpuType = THREE.IntType;
  third.gpuType = THREE.IntType;
  geometry.setAttribute("attr0", first);
  geometry.setAttribute("attr1", second);
  geometry.setAttribute("attr2", third);
  geometry.setIndex(new THREE.Uint32BufferAttribute(new Uint32Array(group.indices), 1));
  geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(16, 16, 16), Math.sqrt(3) * 16);
  return geometry;
}
